import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { StepUpAuthService } from './step-up-auth.service';
import { globalMessages } from '../global-message';

/**
 * Lines of business that have restricted (read-only) for Policy Servicing for access even at Level 2.
 */
const POLICY_SERVICING_RESTRICTED_LOBS: string[] = [
  'Street',
  'Offroad',
  'MCST',
  'MCOR',
  'CPL',
  'MH',
];

/**
 * Centralized service to determine the current user's access capabilities.
 *
 * Access rules:
 * - Level 1: Read-only. User cannot perform any actions.
 * - Level 2 + restricted LOB (Street & Offroad): Limited access. User can view
 *   but cannot modify payment methods, make payments, etc.
 * - Level 2 + standard LOB: Full access.
 *
 * Components inject this service and use the computed signals to conditionally
 * enable/disable features.
 */
@Injectable({ providedIn: 'root' })
export class UserAccessService {
  private readonly stepUpAuth = inject(StepUpAuthService);
  private readonly store = inject(Store);

  /** Policy data from the store */
  private readonly storeData = toSignal(
    this.store.select((state: any) => state.policy?.data),
    { initialValue: null }
  );

  /** Line of business derived from policySnapshot.policyGeneralInformation.lineOfBusiness */
  private readonly lineOfBusiness = computed(() => {
    const policyNumber = this.stepUpAuth.getPolicyNumber();
    const data = this.storeData();
    return (
      data?.[policyNumber]?.policySnapshot?.policyInfo?.product ?? ''
    );
  });

  /** Policy status derived from policySnapshot.policyGeneralInformation.policyStatus */
  private readonly policyStatus = computed(() => {
    const policyNumber = this.stepUpAuth.getPolicyNumber();
    const data = this.storeData();
    return (
      data?.[policyNumber]?.policySnapshot?.policyInfo?.status ?? ''
    );
  });

  /** Whether the policy is cancelled */
  readonly isPolicyNotInForce = computed(() => {
    const status = this.policyStatus().toLowerCase().trim();
    return (
      status.toLowerCase() === globalMessages.policyStatusCancelled ||
      status.toLowerCase() === globalMessages.policyStatusExpired ||
      status.toLowerCase() === globalMessages.policyStatusCancelPending
    );
  });

  /** Current auth level from step-up auth */
  readonly authLevel = this.stepUpAuth.authLevel;

  /** Whether user is authenticated at Level 1 */
  readonly isLevel1 = computed(() => this.authLevel() === 'level1');

  /** Whether user is authenticated at Level 2 */
  readonly isLevel2 = computed(() => this.authLevel() === 'level2');

  /** Whether the current LOB is restricted (e.g., Street & Offroad) */
  readonly isRestrictedLob = computed(() => {
    const lob = this.lineOfBusiness().toLowerCase().trim();
    return POLICY_SERVICING_RESTRICTED_LOBS.some((restricted) => lob.includes(restricted));
  });

  /**
   * Whether the user has full access (can perform all actions).
   * True only when Level 2 AND LOB is not restricted AND policy is not cancelled.
   */
  readonly hasFullAccess = computed(
    () => this.isLevel2() && !this.isRestrictedLob() && !this.isPolicyNotInForce()
  );

  /**
   * Whether the user can make payments.
   * Whether the user can modify payment methods (add/edit/delete/set default).
   * Whether the user can update contact preferences (email, phone).
   * Denied for Level 1 users and restricted LOB users.
   */
  readonly hasEditableAccess = computed(
    () =>
      (this.isLevel1() || this.isLevel2()) && !this.isRestrictedLob() && !this.isPolicyNotInForce()
  );

  /** Get the current line of business value */
  getLineOfBusiness(): string {
    return this.lineOfBusiness();
  }
}
